import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useImportMathToolsDrawing } from 'customHooks/canvas';
import { EventBus } from 'events/EventBus';
import { ReaderToolsEvent } from 'events/EventTypes';
import Icon from 'components/Icon';
import styles from './index.module.scss';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { useStore, StoreTypes } from 'context';
import { useFetchBooks } from 'customHooks/book';
import { useWindowSize } from 'customHooks/windowSize';
import { domToBlob } from 'modern-screenshot';
import { ReaderZoomType } from 'constants/ReaderTools';
import { useGAEvent } from 'components/AppService/GAService';
import * as types from 'constants/actionTypes';

const fontDataUriCache = new Map();

function extractUrlFromCssUrlToken(token) {
  if (!token) return null;
  const trimmed = token.trim();
  if (!trimmed) return null;
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
}

async function fetchFontAsDataUri(url) {
  if (fontDataUriCache.has(url)) return fontDataUriCache.get(url);
  const resp = await fetch(url, { cache: 'force-cache' });
  if (!resp.ok) return null;
  const buf = await resp.arrayBuffer();
  const mime = resp.headers.get('content-type') || 'font/woff';
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 8192) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
  }
  const dataUri = `url("data:${mime};base64,${btoa(binary)}")`;
  fontDataUriCache.set(url, dataUri);
  return dataUri;
}

async function inlineSvgFonts(containerEl) {
  const styleEls = Array.from(containerEl.querySelectorAll('style'));
  const restorers = [];

  for (const styleEl of styleEls) {
    const original = styleEl.textContent;
    if (!original.includes('@font-face')) continue;

    const urlMatches = [...original.matchAll(/url\(([^)]+)\)/g)];
    if (urlMatches.length === 0) continue;

    let updated = original;
    for (const [full, rawUrlToken] of urlMatches) {
      const url = extractUrlFromCssUrlToken(rawUrlToken);
      if (!url || url.startsWith('data:') || url.startsWith('#')) continue;
      try {
        const dataUri = await fetchFontAsDataUri(url);
        if (dataUri) updated = updated.split(full).join(dataUri);
      } catch {}
    }

    if (updated !== original) {
      styleEl.textContent = updated;
      restorers.push(() => { styleEl.textContent = original; });
    }
  }

  return () => restorers.forEach(r => r());
}

async function waitForFontsReady(containerEl, timeoutMs = 2000) {
  try {
    const doc = (containerEl && containerEl.ownerDocument) || document;
    const fonts = doc && doc.fonts;
    if (!fonts || !fonts.ready || typeof fonts.ready.then !== 'function') return;
    await Promise.race([
      fonts.ready,
      new Promise(resolve => setTimeout(resolve, timeoutMs)),
    ]);
  } catch {}
}

function getAdaptiveCaptureScale(targetEl) {
  const width = (targetEl && targetEl.offsetWidth) || 0;
  const dpr = window.devicePixelRatio || 1;
  const minReadableWidth = 1800;
  const widthBoost = width > 0 ? (minReadableWidth / width) : 1;
  const preferred = Math.max(2, dpr * 1.5, widthBoost * 2);
  return Math.min(4, Math.max(2, preferred));
}

async function waitForRenderSettled() {
  await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
}

function isWindowsPlatform() {
  try {
    if (navigator.userAgentData && navigator.userAgentData.platform) {
      return /windows/i.test(navigator.userAgentData.platform);
    }
    return /win/i.test(navigator.platform || '') || /windows/i.test(navigator.userAgent || '');
  } catch {
    return false;
  }
}

function applyCaptureFontFallback(containerEl) {
  const doc = (containerEl && containerEl.ownerDocument) || document;
  if (!containerEl || !doc || !isWindowsPlatform()) return () => {};

  const styleEl = doc.createElement('style');
  styleEl.setAttribute('data-capture-font-fallback', 'true');
  styleEl.textContent = `
    #studentScreenshotArea, #studentScreenshotArea * {
      text-rendering: geometricPrecision;
    }
    #studentScreenshotArea p,
    #studentScreenshotArea span,
    #studentScreenshotArea div,
    #studentScreenshotArea li,
    #studentScreenshotArea td,
    #studentScreenshotArea th,
    #studentScreenshotArea sup,
    #studentScreenshotArea sub,
    #studentScreenshotArea em,
    #studentScreenshotArea strong,
    #studentScreenshotArea b,
    #studentScreenshotArea i {
      font-family: "Noto Sans CJK TC", "Microsoft JhengHei", "Microsoft JhengHei UI", "PingFang TC", "Heiti TC", "Segoe UI Symbol", "Cambria Math", sans-serif !important;
    }
  `;
  containerEl.appendChild(styleEl);
  return () => {
    if (styleEl.parentNode) styleEl.parentNode.removeChild(styleEl);
  };
}

const MathToolsContainer = ({ mathToolsInfo }) => {
  const { type } = mathToolsInfo
  const [ifraRef, setContentRef] = useState(null)
  const importMathToolsDrawing = useImportMathToolsDrawing();
  const waitJsonResolveRef = useRef(null)
  const [{ isDoublePageMode, pageIndex }] = useStore(StoreTypes.reader);
  const [{ books, bookId, bookContent, style }] = useStore(StoreTypes.books);
  const [, globalDispatch] = useStore(StoreTypes.global);
  const { gaEvent } = useGAEvent();
  const windowSize = useWindowSize();
  const { fetchBook } = useFetchBooks();
  const closeLeftHandler = () => {
    closeRightHandler();
  }
  const closeRightHandler = async () => {
    const importAndClose = async () => {
      try {
        if (!ifraRef || !ifraRef.contentWindow) throw new Error('iframe not ready');
        let mappingInfo = null;
        try {
          const toolDoc = ifraRef.contentWindow.document;
          const toolContainer = toolDoc && toolDoc.getElementById('animation_container');
          const toolCanvas = toolDoc && toolDoc.getElementById('canvas');
          const readerCanvas = document.querySelector('canvas.lower-canvas');
          const iframeRect = ifraRef.getBoundingClientRect();
          const targetToolEl = toolContainer || toolCanvas;
          if (targetToolEl && readerCanvas) {
            mappingInfo = {
              toolRect: targetToolEl.getBoundingClientRect(),
              readerRect: readerCanvas.getBoundingClientRect(),
              iframeRect,
              baseW: 1400,
              baseH: 1050
            };
          }
        } catch (e) {}
        let jsonStr = null;
        try {
          if (typeof ifraRef.contentWindow.exportDrawingJSON === 'function') {
            jsonStr = ifraRef.contentWindow.exportDrawingJSON();
          }
        } catch (e) {}
        if (!jsonStr) {
          jsonStr = await new Promise((resolve) => {
            waitJsonResolveRef.current = resolve;
            ifraRef.contentWindow.postMessage({ msg: 'ExportMathToolsJSON' }, '*');
            setTimeout(() => resolve(null), 100);
          });
        }
        if (jsonStr) await importMathToolsDrawing({ json: jsonStr, mappingInfo });
      } catch (e) {
      } finally {
        EventBus.emit({ event: ReaderToolsEvent.ClickMathToolsEvent, payload: { mathToolsInfo: null } });
      }
    };

    confirmAlert({
      title: '是否儲存畫記到電子書',
      message: null,
      buttons: [
        {
          label: '是',
          onClick: () => {
            gaEvent('工具列_尺規作圖_儲存畫記');
            importAndClose();
          }
        },
        {
          label: '否',
          onClick: () => {
            gaEvent('工具列_尺規作圖_不儲存畫記');
            EventBus.emit({ event: ReaderToolsEvent.ClickMathToolsEvent, payload: { mathToolsInfo: null } });
          }
        }
      ],
      closeOnEscape: true,
      closeOnClickOutside: true
    });
  }

  useEffect(() => {
    var MathToolsButtonGAMap = {
      '拖曳': '工具列_尺規作圖_拖曳',
      '畫筆': '工具列_尺規作圖_畫筆',
      '線段': '工具列_尺規作圖_線段',
      '圓規': '工具列_尺規作圖_圓規',
      '直尺': '工具列_尺規作圖_直尺',
      '白色背景': '工具列_尺規作圖_白色背景',
      '回復': '工具列_尺規作圖_回復',
      '取消回復': '工具列_尺規作圖_取消回復',
      '全刪': '工具列_尺規作圖_全刪',
    };
    const handler = (event) => {
      const { data } = event;
      if (!data) return;
      if (data.type === 'MathToolsJSON') {
        if (waitJsonResolveRef.current) {
          waitJsonResolveRef.current(data.payload || null);
          waitJsonResolveRef.current = null;
        }
      } else if (data.type === 'MathToolsButtonClick') {
        var gaName = MathToolsButtonGAMap[data.payload];
        if (gaName) gaEvent(gaName);
      }
    }
    window.addEventListener('message', handler, false);
    return () => window.removeEventListener('message', handler);
  }, [gaEvent])

  const iframeOnLoad = useCallback(() => {
    if (!ifraRef) return;
    setTimeout(() => {
      const run = async () => {
        try {
          const waitForResizeBook = async () => {
            EventBus.emit({
              event: ReaderToolsEvent.SetReaderZoomEvent,
              payload: { type: ReaderZoomType.OriginZoom },
            });
            await new Promise(resolve => setTimeout(resolve, 100));
          };

          if (bookId) {
            let pages = isDoublePageMode ? [pageIndex * 2, pageIndex * 2 + 1] : [pageIndex];
            pages = pages.filter(p => !bookContent || !bookContent[p]);
            if (pages.length > 0) {
              fetchBook({ bookId, pages });
            }
          }

          const bookInfo = (books || []).find(b => b.bookId === bookId);

          if (ifraRef.contentWindow && bookInfo) {
            await waitForResizeBook();
            const targetEl = document.querySelector('#studentScreenshotArea');
            const readerView = document.getElementById('readerView');
            if (targetEl) {
              // Electron 已在 overlay 掛載前用原生 capturePage 預先截好畫面(見 ReaderEventHandler)，
              // 此時直接用該 dataURL,完全跳過 domToBlob 與字型 inline / fallback;
              // 網頁環境沒有 electronCapture,維持原本 domToBlob 流程。
              const electronCapture = mathToolsInfo && mathToolsInfo.electronCapture;
              let restoreFonts = () => {};
              let restoreCaptureFontFallback = () => {};
              try {
                let dataUrl;
                if (electronCapture && electronCapture.dataUrl) {
                  dataUrl = electronCapture.dataUrl;
                } else {
                  await waitForFontsReady(targetEl);
                  restoreFonts = await inlineSvgFonts(targetEl);
                  restoreCaptureFontFallback = applyCaptureFontFallback(targetEl);
                  await waitForFontsReady(targetEl);
                  await waitForRenderSettled();
                  const captureScale = getAdaptiveCaptureScale(targetEl);
                  const blob = await domToBlob(targetEl, {
                    type: 'image/png',
                    width: targetEl.offsetWidth,
                    height: targetEl.offsetHeight,
                    backgroundColor: 'white',
                    scale: captureScale,
                    fetch: {
                      requestInit: { cache: 'no-cache' },
                    },
                  });
                  dataUrl = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                  });
                }
                const images = [{ pageIndex: 0, svg: dataUrl }];
                let contentBounds = null;
                const shotRect = targetEl.getBoundingClientRect();
                const LRGapValue = (windowSize && windowSize.width != null && style && style.width != null)
                  ? Math.max(0, Math.round((windowSize.width - style.width) * 0.5))
                  : 0;
                if (readerView) {
                  const rvRect = readerView.getBoundingClientRect();
                  contentBounds = {
                    x: Math.max(0, Math.round(rvRect.left - shotRect.left + LRGapValue)),
                    y: Math.max(0, Math.round(rvRect.top - shotRect.top)),
                    width: Math.max(0, Math.round(rvRect.width - LRGapValue * 2)),
                    height: Math.max(0, Math.round(rvRect.height)),
                  };
                }
                const savedViewport = mathToolsInfo && mathToolsInfo.savedViewport;
                ifraRef.contentWindow.postMessage({ type: 'ReaderPageData', payload: { images, width: targetEl.offsetWidth, height: targetEl.offsetHeight, contentBounds, lrGap: LRGapValue, fullWidthMode: true, savedViewport } }, '*');
              } catch (e) {
              } finally {
                restoreCaptureFontFallback();
                restoreFonts();
              }
            }
          }
        } catch (e) {
        } finally {
          createMathTools(type, ifraRef.contentWindow.flashRoot)
          // NBK-1559：iframe(尺規作圖)載入、截圖與工具初始化完成後才顯示提示，
          // 避免提示在截圖時機出現而被 electron capturePage / web domToBlob 截入課本頁面。
          globalDispatch({
            type: types.SET_GROUP_INTERACTIVITY_SNACKBAR,
            payload: {
              message: "為確保作圖準確，已切換為固定比例",
              style: "success",
              actionHandler: null,
            },
          });
        }
      };
      run();
    }, 300);
  }, [type, mathToolsInfo, ifraRef, books, bookId, isDoublePageMode, pageIndex, fetchBook, bookContent, style, windowSize, globalDispatch])

  const createMathTools = (type, flashRoot) => {
    flashRoot.mathToolsInit(type);
  }

  return (
    <div className={styles.mathToolsContainer}>
      <iframe ref={setContentRef} className={styles.iframe} onLoad={() => iframeOnLoad()} title="compass" src="assets/MathTools/MathTools.html" frameborder="0"></iframe>
      <div className={styles.closeLeftButton} onClick={closeLeftHandler}><Icon name="Close" /></div>
      <div className={styles.closeRightButton} onClick={closeRightHandler}><Icon name="Close" /></div>
    </div>
  )
}

export default MathToolsContainer;